import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import { getTenantPortalLoginUrl, getTenantPortalLabel } from '../../utils/tenantPortalUrl';
import { portalInviteButtonLabel, portalStatusBadge, resolveTenantPortalStatus } from '../../utils/tenantPortalStatus';
import { PROPERTY_PAGE_CSS } from './propertyPageStyles';
import { UK, effectiveStatus, rtrBadge, leaseLine, ini } from './tenantFormShared';

const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB') : '—';

function Spec({ label, value, muted }: { label: string; value?: string | number | null; muted?: boolean }) {
  return (
    <div className="pp-spec-item">
      <label>{label}</label>
      <span className={!value && value !== 0 ? 'muted' : ''}>{value ?? '—'}</span>
    </div>
  );
}

function TenantAvatar({ t, size = 56 }: { t: any; size?: number }) {
  if (t.image_url) {
    return (
      <img src={t.image_url} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '3px solid #fce7f3', flexShrink: 0 }} />
    );
  }
  const colors = ['#012169', '#16a34a', '#E6A817', '#C8102E', '#0d9488'];
  const bg = colors[(t.full_name?.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: Math.floor(size * 0.35), flexShrink: 0, border: '3px solid #fce7f3' }}>
      {ini(t.full_name)}
    </div>
  );
}

export default function TenantViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [emailingStatement, setEmailingStatement] = useState(false);

  const portalUrl = getTenantPortalLoginUrl();

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/landlord/tenants/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Not found');
      setData(await r.json());
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => { load(); }, [load]);

  const invitePortal = async () => {
    const t = data?.tenant;
    if (!t?.email) { showToast('Add an email address first'); return; }
    const status = resolveTenantPortalStatus(t);
    const msg = status === 'invited' || status === 'invite_expired'
      ? `Resend portal invite to ${t.email}?`
      : `Send portal invite to ${t.email}?`;
    if (!window.confirm(msg)) return;
    setInviting(true);
    try {
      const res = await fetch(`${API_URL}/landlord/tenants/${id}/invite-portal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      showToast(res.ok ? `Invite sent to ${t.email}` : d.error || 'Failed');
      if (res.ok) load();
    } catch { showToast('Network error'); }
    finally { setInviting(false); }
  };

  const copyPortalLink = () => {
    navigator.clipboard?.writeText(portalUrl);
    showToast('Tenant portal link copied');
  };

  const downloadStatementPDF = async () => {
    const tenant = data?.tenant;
    if (!id || !tenant?.full_name) return;
    const r = await fetch(`${API_URL}/landlord/tenants/${id}/statement/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { showToast('Failed to download statement'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statement-${tenant.full_name.replace(/\s+/g, '-')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const emailStatement = async () => {
    const tenant = data?.tenant;
    if (!id || !tenant?.email) { showToast('Tenant has no email'); return; }
    setEmailingStatement(true);
    const r = await fetch(`${API_URL}/landlord/tenants/${id}/statement/email`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({}),
    });
    const d = await r.json();
    showToast(r.ok ? `Statement sent to ${d.sent_to}` : d.error || 'Failed');
    setEmailingStatement(false);
  };

  const archiveTenant = async () => {
    const tenant = data?.tenant;
    if (!window.confirm(`Archive ${tenant?.full_name}?`)) return;
    const r = await fetch(`${API_URL}/landlord/tenants/${id}/archive`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { showToast('Tenant archived'); navigate('/landlord/tenants'); }
    else showToast('Failed to archive');
  };

  if (loading) {
    return (
      <div className="pp-page">
        <style>{PROPERTY_PAGE_CSS}</style>
        <div style={{ textAlign: 'center', padding: 80, color: '#9ca3af' }}>Loading tenant…</div>
      </div>
    );
  }

  if (!data?.tenant) {
    return (
      <div className="pp-page">
        <style>{PROPERTY_PAGE_CSS}</style>
        <div style={{ textAlign: 'center', padding: 80 }}>
          <p style={{ fontWeight: 700, color: '#374151' }}>Tenant not found</p>
          <button type="button" className="pp-btn pp-btn-outline" style={{ marginTop: 16 }} onClick={() => navigate('/landlord/tenants')}>← Back to Tenants</button>
        </div>
      </div>
    );
  }

  const t = data.tenant;
  const rb = rtrBadge(t);
  const status = effectiveStatus(t);
  const activeLeases = t.active_leases || [];
  const portalStatus = resolveTenantPortalStatus(t);
  const portalBadge = portalStatusBadge(portalStatus);

  return (
    <div className="pp-page">
      <style>{PROPERTY_PAGE_CSS}{`
        .tv-portal-box { background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%); border: 1.5px solid #f9a8d4; border-radius: 12px; padding: 18px 20px; margin-bottom: 16px; }
        .tv-portal-url { font-family: 'DM Mono', monospace; font-size: 13px; color: ${UK.blue}; word-break: break-all; }
        .pp-btn-pink { background: linear-gradient(145deg, #db2777, #ec4899); color: #fff; border: none; }
        .pp-btn-pink:hover { filter: brightness(1.05); }
      `}</style>

      <header className="pp-top">
        <div className="pp-top-inner">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <TenantAvatar t={t} size={56} />
            <div>
            <button type="button" className="pp-back" onClick={() => navigate('/landlord/tenants')}>← Tenants</button>
            <h1 className="pp-title">{t.full_name}</h1>
            <div className="pp-meta">
              <span>{t.email || 'No email'}</span>
              {t.phone && <><span>·</span><span>{t.phone}</span></>}
              <span>·</span>
              <span className="pp-badge pp-badge-blue">{status.replace(/_/g, ' ')}</span>
              <span className="pp-badge" style={{ background: rb.bg, color: rb.color }}>{rb.label}</span>
              {t.portal_active && <span className="pp-badge pp-badge-green">Portal active</span>}
              {!t.portal_active && <span className="pp-badge" style={{ background: portalBadge.bg, color: portalBadge.color }}>{portalBadge.label}</span>}
            </div>
            </div>
          </div>
          <div className="pp-actions">
            <button type="button" className="pp-btn pp-btn-outline" onClick={downloadStatementPDF}>Statement PDF</button>
            <button type="button" className="pp-btn pp-btn-outline" onClick={emailStatement} disabled={emailingStatement}>{emailingStatement ? 'Sending…' : 'Email statement'}</button>
            <button type="button" className="pp-btn pp-btn-pink" onClick={invitePortal} disabled={inviting || portalStatus === 'active'}>{portalInviteButtonLabel(portalStatus, inviting)}</button>
            <button type="button" className="pp-btn pp-btn-outline" onClick={() => navigate(`/landlord/tenants/${id}/edit`)}>Edit</button>
            <button type="button" className="pp-btn pp-btn-primary" onClick={() => navigate(`/landlord/leases/new?tenant_id=${t.id}`, { state: { tenantId: t.id, tenantName: t.full_name, propertyAddress: t.property_address } })}>+ Add lease</button>
            <button type="button" className="pp-btn pp-btn-outline" style={{ color: UK.red, borderColor: '#fecaca' }} onClick={archiveTenant}>Archive</button>
          </div>
        </div>
      </header>

      <div className="pp-body">
        <div className="tv-portal-box">
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9d174d', marginBottom: 8 }}>Tenant portal — share with your tenant</div>
          <div className="tv-portal-url">{getTenantPortalLabel()}</div>
          <p style={{ margin: '8px 0 12px', fontSize: 13, color: '#831843', lineHeight: 1.5 }}>
            Tenants sign in here to view rent, maintenance, and documents. Use <strong>Invite to portal</strong> to email them a set-up link, or copy this URL.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="pp-btn pp-btn-pink" onClick={copyPortalLink}>Copy portal link</button>
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="pp-btn pp-btn-outline" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Open portal ↗</a>
            {t.email && (
              <button type="button" className="pp-btn pp-btn-outline" onClick={() => navigate('/send-message', { state: { recipients: [{ name: t.full_name, email: t.email }] } })}>Email tenant</button>
            )}
          </div>
        </div>

        <div className="pp-grid-2">
          <div>
            <section className="pp-section">
              <h2 className="pp-section-title">Contact & tenancy</h2>
              <div className="pp-spec-grid">
                <Spec label="Email" value={t.email} />
                <Spec label="Phone" value={t.phone} />
                <Spec label="Property (record)" value={t.property_address} />
                <Spec label="Room" value={t.room_number} />
                <Spec label="Move in" value={fmtDate(t.move_in_date)} />
                <Spec label="Occupants" value={t.occupants} />
              </div>
            </section>

            <section className="pp-section">
              <h2 className="pp-section-title">Right to Rent</h2>
              <div className="pp-spec-grid">
                <Spec label="Check date" value={fmtDate(t.right_to_rent_checked)} />
                <Spec label="Document" value={t.right_to_rent_doc_type} />
                <Spec label="Expiry" value={fmtDate(t.right_to_rent_expiry)} />
                <Spec label="Share code" value={t.right_to_rent_share_code} />
              </div>
            </section>

            {(t.guarantor_name || t.emergency_contact_name) && (
              <section className="pp-section">
                <h2 className="pp-section-title">Guarantor & emergency</h2>
                <div className="pp-spec-grid">
                  <Spec label="Guarantor" value={t.guarantor_name} />
                  <Spec label="Guarantor phone" value={t.guarantor_phone} />
                  <Spec label="Emergency contact" value={t.emergency_contact_name} />
                  <Spec label="Emergency phone" value={t.emergency_contact_phone} />
                </div>
              </section>
            )}
          </div>

          <div>
            <section className="pp-section">
              <h2 className="pp-section-title">Active leases ({activeLeases.length})</h2>
              {activeLeases.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>No active lease — <Link to="/landlord/leases/new" style={{ color: UK.blue, fontWeight: 700 }}>create one</Link></p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {activeLeases.map((l: any) => (
                    <div key={l.id} style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 10, background: '#f9fafb' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#111' }}>{leaseLine(l)}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        £{Number(l.monthly_rent || 0).toLocaleString()}/mo · ends {fmtDate(l.end_date)}
                      </div>
                      <button type="button" className="pp-btn pp-btn-outline" style={{ marginTop: 10, padding: '6px 12px', fontSize: 12 }}
                        onClick={() => navigate(`/landlord/leases/${l.id}`)}>View lease →</button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {data.referencing && (
              <section className="pp-section">
                <h2 className="pp-section-title">Referencing</h2>
                <div className="pp-spec-grid">
                  <Spec label="Credit" value={data.referencing.credit_check} />
                  <Spec label="Employment" value={data.referencing.employment_check} />
                  <Spec label="Landlord ref" value={data.referencing.landlord_reference} />
                  <Spec label="Deposit received" value={data.referencing.deposit_received} />
                </div>
              </section>
            )}

            <section className="pp-section">
              <h2 className="pp-section-title">Quick actions</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button type="button" className="pp-btn pp-btn-outline" onClick={() => navigate('/landlord/rent')}>Rent tracker →</button>
                <button type="button" className="pp-btn pp-btn-outline" onClick={() => navigate('/landlord/maintenance')}>Maintenance →</button>
                <button type="button" className="pp-btn pp-btn-outline" onClick={() => navigate(`/landlord/tenants/${id}/edit`)}>Edit full record →</button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
